import { useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { useCgmDays } from '@/hooks/queries';
import { positiveIntId } from '@/lib/schemas';

export default function CgmReport() {
  // Outer guard avoids the rules-of-hooks violation: this component runs
  // no hooks after the conditional Navigate. The real work lives in the
  // body, which only mounts with a validated patientId.
  const { patientId } = useParams();
  const parsed = positiveIntId.safeParse(patientId);
  if (!parsed.success) return <Navigate to="/patients" replace />;
  return <CgmReportBody patientId={parsed.data} />;
}

function CgmReportBody({ patientId }: { patientId: number }) {
  const { t } = useTranslation();
  const days = useCgmDays(patientId);

  if (days.isLoading) return <Skeleton className="m-6 h-72 w-full max-w-4xl" />;
  if (!days.data || days.data.length === 0) {
    return (
      <div className="p-6">
        <EmptyState title={t('No CGM data available')} />
      </div>
    );
  }

  const flat = days.data.flatMap((d) => d.entries.map((e) => ({ time: e.recorded_time, glucose: e.glucose_value })));

  return (
    <div className="p-6">
      <Card className="mx-auto max-w-5xl">
        <CardHeader>
          <CardTitle>{t('CGM glucose trend')}</CardTitle>
        </CardHeader>
        <CardContent className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={flat}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="glucose" stroke="#36C2B4" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
